import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, LogOut, ChevronRight } from 'lucide-react';
import ExpanseLogo from '../assets/ExpanseLogo.svg?url';
import ExpansePassportLogoTransparent from '../assets/ExpansePassportLogoTransparent.png?url';

import LanguageSwitcher from './LanguageSwitcher';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
    setIsMobileMenuOpen(false);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const NavLink: React.FC<{ to: string; children: React.ReactNode; mobile?: boolean }> = ({ to, children, mobile }) => {
    const active = isActiveRoute(to);

    if (mobile) {
      return (
          <Link
          to={to}
          onClick={closeMobileMenu}
          className={`group flex items-center justify-between px-4 py-3 text-sm font-medium transition-all rounded-xl ${
            active
              ? 'bg-slate-900 text-white shadow-md'
              : 'text-slate-700 hover:bg-slate-200 hover:text-slate-900 hover:shadow-sm'
          }`}
        >
          <span>{children}</span>
          {!active && (
            <div className="flex items-center justify-center w-5 h-5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronRight className="w-3 h-3" />
            </div>
          )}
        </Link>
      );
    }

    return (
      <Link
        to={to}
        className={`relative px-3 py-2 text-sm font-medium transition-colors ${
          active
            ? 'text-slate-900'
            : 'text-slate-600 hover:text-slate-900'
        }`}
      >
        {children}
        {active && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-900 rounded-full" />
        )}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-50 no-print">
        <div className="relative bg-white/60 backdrop-blur-md" style={{ maskImage: 'linear-gradient(to bottom, black 20%, black 60%, transparent 100%)', WebkitMaskImage: 'linear-gradient(to bottom, black 20%, black 60%, transparent 100%)' }}>
          <div className="px-4 sm:px-6 lg:px-8 mx-auto">
            <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-3 group">
              <div className="w-9 h-9 bg-slate-900 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                <img src={ExpanseLogo} alt="Expanse" className="w-6 h-6 brightness-0 invert" />
              </div>
              <img
                src={ExpansePassportLogoTransparent}
                alt="Expanse"
                className="h-10 w-auto mx-auto"
                style={{ maxWidth: '120px' }}
              />
            </Link>

            {user && (
              <div className="flex items-center gap-3">
                {/* Profile Indicator */}
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-slate-900 text-white rounded-lg flex items-center justify-center overflow-hidden">
                    {user.profilePhotoUrl ? (
                      <img
                        src={user.profilePhotoUrl}
                        alt={user.firstName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-xs font-semibold">
                        {user.firstName.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>
                  <span className="text-sm font-medium text-slate-700 hidden sm:block">
                    {user.firstName}
                  </span>
                </div>

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="flex text-slate-600 hover:text-slate-900 transition-colors p-2 hover:bg-slate-100 rounded-lg"
                  aria-label="Toggle menu"
                >
                  {isMobileMenuOpen ? (
                    <X className="w-5 h-5" />
                  ) : (
                    <Menu className="w-5 h-5" />
                  )}
                </button>
              </div>
            )}
            </div>
          </div>
        </div>
      </header>

      {/* Full-Screen Centered Navigation Menu with Blurred Background */}
      {user && isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xl no-print animate-in fade-in duration-300"
          onClick={closeMobileMenu}
        >
          <div
            className="relative w-full md:max-w-md mx-4 bg-white backdrop-blur-lg rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={closeMobileMenu}
              className="absolute top-3 right-3 p-3 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded-full transition-all z-20 touch-manipulation"
              aria-label="Close menu"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Logo Section */}
            {user.profilePhotoUrl && <img
              src={ExpanseLogo}
              alt="Expanse Passport"
              className="h-6 w-auto ml-6 mt-6"
            />}

            {/* User Info Section */}
            <div className="px-6 pt-6 pb-4 relative">
              <div className="flex flex-col items-center text-center relative z-10">
                <div className="w-16 h-16 text-white rounded-2xl flex items-center justify-center shadow-lg mb-3 overflow-hidden relative">
                  <div className="absolute w-full h-full bg-slate-900 opacity-100 rounded-xl flex items-center justify-center transition-transform">
                    <img src={ExpanseLogo} alt="Expanse" className="h-[50%] brightness-0 invert" style={{ height: '50%' }} />
                  </div>
                  <div className="relative z-10 w-full h-full flex items-center justify-center">
                    {user.profilePhotoUrl ? (
                      <img
                        src={user.profilePhotoUrl}
                        alt={`${user.firstName} ${user.lastName}`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      null
                    )}
                  </div>
                </div>
                <h2 className="text-lg font-semibold text-slate-900 mb-0.5">
                  {user.firstName} {user.lastName}
                </h2>
                <p className="text-xs text-slate-500 capitalize mb-3">
                  {user.role.replace('_', ' ')}
                </p>
                <div className="scale-90 origin-center">
                  <LanguageSwitcher />
                </div>
              </div>
            </div>

            {/* Navigation Links - Centered */}
            <nav className="py-2 px-4">
              <div className="space-y-1">
                <NavLink to="/dashboard" mobile>{t('navigation.dashboard')}</NavLink>
                <NavLink to="/client-management" mobile>{t('navigation.clients')}</NavLink>
                <NavLink to="/qr-scanner" mobile>{t('navigation.qrScanner')}</NavLink>
                {(user?.role === 'ngo_admin' || user?.role === 'system_admin') && (
                  <>
                    <NavLink to="/cr-card-generator" mobile>{t('navigation.qrCardGenerator')}</NavLink>
                    <NavLink to="/field-workers" mobile>{t('navigation.Team')}</NavLink>
                    <NavLink to="/billing" mobile>{t('navigation.billing')}</NavLink>
                  </>
                )}
                {user?.role === 'system_admin' && (
                  <NavLink to="/organizations" mobile>{t('Organizations')}</NavLink>
                )}
              </div>
            </nav>

            {/* Logout Button */}
            <div className="px-4 pb-6 pt-4">
              <button
                onClick={handleLogout}
                className="w-full px-4 py-3 text-red-600 hover:text-red-700 font-semibold transition-all hover:bg-red-50 rounded-xl flex items-center justify-center gap-2 border border-red-100 hover:border-red-200 text-sm"
              >
                <LogOut className="w-4 h-4" />
                <span>{t('navigation.logout')}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      {user && (
        <footer className="bg-white border-t border-slate-200 mt-auto no-print">
          <div className="px-4 py-6">
            <div className="text-center text-xs text-slate-500">
              {t('footer.copyright')}
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default Layout;